/**
 * AttackHistory Component
 *
 * Displays attacks in a scrollable panel in chronological order.
 * Format: "1. Player → B5 — Hit!", "2. AI → D3 — Miss", etc.
 * Auto-scrolls to the latest attack.
 *
 * Requirements: 16.1, 16.2, 16.3, 16.4
 */

import React, { useEffect, useRef } from 'react';
import {
  AttackHistoryProps,
  AttackRecord,
  coordinateToString,
  ShipType,
} from '../types';
import './AttackHistory.css';

/** Display names for ship types */
const SHIP_DISPLAY_NAMES: Record<ShipType, string> = {
  carrier: 'Carrier',
  battleship: 'Battleship',
  cruiser: 'Cruiser',
  submarine: 'Submarine',
  destroyer: 'Destroyer',
};

const formatAttackerName = (attacker: string): string => {
  if (attacker === 'player1') return 'Player';
  return 'AI';
};

const formatResult = (record: AttackRecord): string => {
  switch (record.result) {
    case 'hit':
      return 'Hit!';
    case 'miss':
      return 'Miss';
    case 'sunk':
      return record.sunkShip
        ? `Sunk ${SHIP_DISPLAY_NAMES[record.sunkShip]}!`
        : 'Sunk!';
    default:
      return '';
  }
};

export const AttackHistory: React.FC<AttackHistoryProps> = ({
  attacks,
  currentMoveIndex,
}) => {
  const listRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to latest attack
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [attacks.length]);

  return (
    <div className="bs-attack-history" role="region" aria-label="Attack History">
      <div className="bs-attack-history__title">Attack Log</div>

      <div className="bs-attack-history__list" ref={listRef}>
        {attacks.length === 0 ? (
          <div className="bs-attack-history__empty">
            No attacks yet
          </div>
        ) : (
          attacks.map((record, index) => {
            const isCurrent = currentMoveIndex !== undefined && index === currentMoveIndex;

            return (
              <div
                key={record.moveNumber}
                className={`bs-attack-history__item ${
                  isCurrent ? 'bs-attack-history__item--current' : ''
                }`}
                role="listitem"
                aria-label={`Move ${record.moveNumber}: ${formatAttackerName(
                  record.attacker
                )} attacked ${coordinateToString(record.coordinate)} — ${formatResult(
                  record
                )}`}
              >
                <span className="bs-attack-history__move-num">
                  {record.moveNumber}.
                </span>
                <span className="bs-attack-history__attacker">
                  {formatAttackerName(record.attacker)}
                </span>
                <span>→</span>
                <span className="bs-attack-history__coord">
                  {coordinateToString(record.coordinate)}
                </span>
                <span>—</span>
                <span
                  className={`bs-attack-history__result bs-attack-history__result--${record.result}`}
                >
                  {formatResult(record)}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AttackHistory;
